import React, { useState, useEffect } from 'react';
import { rptApi, rptV2Api, rptV3Api } from '../services/api';
import '../styles/Panel.css';

function GLListPanelEnhanced({ entity, refreshTrigger, onGenerateRPT, onGenerateRPTV2, onGenerateRPTV3, onViewGL }) {
  const [loading, setLoading] = useState(true);
  const [glVersions, setGlVersions] = useState([]);
  const [rptVersions, setRptVersions] = useState([]);
  const [rptV2Versions, setRptV2Versions] = useState([]);
  const [rptV3Versions, setRptV3Versions] = useState([]);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [error, setError] = useState(null);
  const [v2Eligible, setV2Eligible] = useState(false);
  const [v3Eligible, setV3Eligible] = useState(false);
  const connectionType = entity?.connection_type?.toUpperCase();
  const isEverlifeXeroManualLike = entity?.finalyzer_company_code === 'EH'
    && connectionType === 'XERO'
    && entity?.sap_database === 'N/A';
  const isManualUploadEntity = Boolean(entity?.manual_gl_upload_enabled)
    || ['N/A', 'MANUAL'].includes(connectionType)
    || isEverlifeXeroManualLike;

  useEffect(() => {
    loadData();
    checkEligibility();
  }, [entity, refreshTrigger]);

  const checkEligibility = async () => {
    if (!entity) return;
    try {
      const [v2Response, v3Response] = await Promise.all([
        rptV2Api.checkEligibility(entity.name),
        rptV3Api.checkEligibility(entity.name)
      ]);
      setV2Eligible(v2Response.eligible);
      setV3Eligible(v3Response.eligible);
    } catch (err) {
      setV2Eligible(false);
      setV3Eligible(false);
    }
  };

  const loadData = async () => {
    if (!entity) return;

    setLoading(true);
    setError(null);

    try {
      // Load GL, RPT V1, and RPT V2 versions
      const [glData, rptData] = await Promise.all([
        rptApi.listGL(entity.name),
        rptApi.getRPTStatus(entity.name)
      ]);

      setGlVersions(glData.versions || []);
      setRptVersions(rptData.versions || []);

      // Try to load V2 reports (may not exist)
      try {
        const v2Data = await rptV2Api.listReports(entity.name);
        setRptV2Versions(v2Data.reports || []);
      } catch (err) {
        setRptV2Versions([]);
      }

      // Try to load V3 reports (may not exist)
      try {
        const v3Data = await rptV3Api.listReports(entity.name);
        setRptV3Versions(v3Data.reports || []);
      } catch (err) {
        setRptV3Versions([]);
      }
    } catch (err) {
      setError(err.message || 'Failed to load data');
      setGlVersions([]);
      setRptVersions([]);
      setRptV2Versions([]);
      setRptV3Versions([]);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const match = timestamp.match(/(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})/);
    if (match) {
      const [, year, month, day, hour, minute, second] = match;
      return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
    }
    return timestamp;
  };

  const toggleRow = (timestamp) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(timestamp)) {
      newExpanded.delete(timestamp);
    } else {
      newExpanded.add(timestamp);
    }
    setExpandedRows(newExpanded);
  };

  const getRelatedRPTs = (glVersion) => {
    // Find RPT versions that were generated from this specific GL version
    // Match by gl_source_timestamp to ensure correct mapping
    return rptVersions.filter(rpt => rpt.gl_source_timestamp === glVersion.timestamp);
  };

  const getRelatedRPTV2s = (glVersion) => {
    // Find V2 RPT versions for this period
    // V2 reports use period instead of gl_source_timestamp
    const glPeriod = glVersion.period;
    return rptV2Versions.filter(rpt => rpt.period === glPeriod);
  };

  const getRelatedRPTV3s = (glVersion) => {
    // Find V3 RPT versions for this period
    const glPeriod = glVersion.period;
    return rptV3Versions.filter(rpt => rpt.period === glPeriod);
  };

  const handleDownloadRPTV3 = async (rptVersion) => {
    try {
      const blob = await rptV3Api.downloadRPT(entity.name, rptVersion.timestamp, rptVersion.period);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rpt_v3_${entity.name}_${rptVersion.timestamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert('Failed to download RPT V3: ' + err.message);
    }
  };

  const handleDownloadRPTV2 = async (rptVersion) => {
    try {
      const blob = await rptV2Api.downloadRPT(entity.name, rptVersion.timestamp, rptVersion.period);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rpt_v2_${entity.name}_${rptVersion.timestamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert('Failed to download RPT V2: ' + err.message);
    }
  };

  const handleDownloadGL = async (glVersion) => {
    try {
      const blob = await rptApi.downloadGL(entity.name, glVersion.period, glVersion.timestamp);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gl_${entity.name}_${glVersion.period}_${glVersion.timestamp}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert('Failed to download GL: ' + (err.message || err));
    }
  };

  const handleDownloadRPT = async (rptVersion) => {
    try {
      const blob = await rptApi.downloadRPT(entity.name, rptVersion.timestamp, rptVersion.period);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rpt_${entity.name}_${rptVersion.timestamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert('Failed to download RPT: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div className="panel gl-list-panel">
        <h2>Available General Ledger Data</h2>
        <div className="loading">Loading GL versions...</div>
      </div>
    );
  }

  return (
    <div className="panel gl-list-panel">
      <h2>Available General Ledger Data</h2>
      <p className="panel-description">
        Select a GL version to generate RPT, view details, or download the existing GL.
        Click the arrow to see related RPT versions.
      </p>

      {error && (
        <div className="alert alert-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {glVersions.length === 0 ? (
        <div className="alert alert-info">
          <p>
            No GL data available. Please {isManualUploadEntity ? 'select a date range and upload a GL file' : 'fetch GL data from SAP'} using Step 1 above.
          </p>
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="gl-list-table">
            <thead>
              <tr>
                <th style={{ width: '40px' }}></th>
                <th>Period</th>
                <th>Timestamp</th>
                <th>Total Entries</th>
                <th>Unique Transactions</th>
                <th>Unique Accounts</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {glVersions.map((version) => {
                const relatedRPTs = getRelatedRPTs(version);
                const relatedRPTV2s = getRelatedRPTV2s(version);
                const relatedRPTV3s = getRelatedRPTV3s(version);
                const hasRelatedItems = relatedRPTs.length > 0 || relatedRPTV2s.length > 0 || relatedRPTV3s.length > 0;
                const isExpanded = expandedRows.has(version.timestamp);

                return (
                  <React.Fragment key={version.timestamp}>
                    {/* GL Row */}
                    <tr>
                      <td>
                        {hasRelatedItems && (
                          <button
                            className="expand-btn"
                            onClick={() => toggleRow(version.timestamp)}
                            title={isExpanded ? 'Collapse' : 'Expand to see RPT versions'}
                          >
                            {isExpanded ? '▼' : '▶'}
                          </button>
                        )}
                      </td>
                      <td>{version.period || 'N/A'}</td>
                      <td className="timestamp-cell">{formatTimestamp(version.timestamp)}</td>
                      <td className="numeric-cell">{version.record_count?.toLocaleString() || 'N/A'}</td>
                      <td className="numeric-cell">{version.unique_transactions?.toLocaleString() || 'N/A'}</td>
                      <td className="numeric-cell">{version.unique_accounts?.toLocaleString() || 'N/A'}</td>
                      <td className="actions-cell">
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => onGenerateRPT(version)}
                          title="Generate RPT V1 from this GL data"
                        >
                          RPT V1
                        </button>
                        {v2Eligible && onGenerateRPTV2 && (
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => onGenerateRPTV2(version)}
                            title="Generate RPT V2 (AI-powered, EL_SAP_B1 entities)"
                            style={{ backgroundColor: '#6366f1' }}
                          >
                            RPT V2 ✨
                          </button>
                        )}
                        {v3Eligible && onGenerateRPTV3 && (
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => onGenerateRPTV3(version)}
                            title="Generate RPT V3 (Rule-based, EL_RPT entities)"
                            style={{ backgroundColor: '#22c55e' }}
                          >
                            RPT V3
                          </button>
                        )}
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => onViewGL(version)}
                          title="View GL details"
                        >
                          View
                        </button>
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() => handleDownloadGL(version)}
                          title="Download the raw GL CSV that was uploaded / fetched"
                        >
                          Download GL
                        </button>
                      </td>
                    </tr>

                    {/* Expanded RPT V1 Rows */}
                    {isExpanded && relatedRPTs.map((rpt) => (
                      <tr key={`rpt-${rpt.timestamp}`} className="rpt-row">
                        <td></td>
                        <td colSpan="2" className="rpt-info">
                          <span className="rpt-label">RPT V1</span>
                          {rpt.status && (
                            <span className={`status-badge badge-${rpt.status}`}>
                              {rpt.status === 'draft' ? 'Draft' : 'Approved'}
                            </span>
                          )}
                          <span className="timestamp-cell" style={{ marginLeft: '0.5rem' }}>
                            {formatTimestamp(rpt.timestamp)}
                          </span>
                        </td>
                        <td className="numeric-cell">{rpt.filtered_records?.toLocaleString() || 'N/A'}</td>
                        <td className="numeric-cell" colSpan="2">{rpt.grouped_records?.toLocaleString() || 'N/A'} grouped</td>
                        <td className="actions-cell">
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => handleDownloadRPT(rpt)}
                            title="Download this RPT V1 version"
                          >
                            Download
                          </button>
                        </td>
                      </tr>
                    ))}

                    {/* Expanded RPT V2 Rows */}
                    {isExpanded && relatedRPTV2s.map((rpt) => (
                      <tr key={`rpt-v2-${rpt.timestamp}`} className="rpt-row rpt-v2-row">
                        <td></td>
                        <td colSpan="2" className="rpt-info">
                          <span className="rpt-label" style={{ backgroundColor: '#6366f1' }}>RPT V2 ✨</span>
                          <span className="timestamp-cell" style={{ marginLeft: '0.5rem' }}>
                            {formatTimestamp(rpt.timestamp)}
                          </span>
                        </td>
                        <td className="numeric-cell">{rpt.filtered_records?.toLocaleString() || 'N/A'}</td>
                        <td className="numeric-cell" colSpan="2">{rpt.total_entries?.toLocaleString() || rpt.grouped_records?.toLocaleString() || 'N/A'} entries</td>
                        <td className="actions-cell">
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => handleDownloadRPTV2(rpt)}
                            title="Download this RPT V2 version"
                          >
                            Download
                          </button>
                        </td>
                      </tr>
                    ))}

                    {/* Expanded RPT V3 Rows */}
                    {isExpanded && relatedRPTV3s.map((rpt) => (
                      <tr key={`rpt-v3-${rpt.timestamp}`} className="rpt-row rpt-v3-row">
                        <td></td>
                        <td colSpan="2" className="rpt-info">
                          <span className="rpt-label" style={{ backgroundColor: '#22c55e' }}>RPT V3</span>
                          <span className="timestamp-cell" style={{ marginLeft: '0.5rem' }}>
                            {formatTimestamp(rpt.timestamp)}
                          </span>
                        </td>
                        <td className="numeric-cell">{rpt.filtered_records?.toLocaleString() || 'N/A'}</td>
                        <td className="numeric-cell" colSpan="2">{rpt.total_entries?.toLocaleString() || rpt.grouped_records?.toLocaleString() || 'N/A'} entries</td>
                        <td className="actions-cell">
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => handleDownloadRPTV3(rpt)}
                            title="Download this RPT V3 version"
                          >
                            Download
                          </button>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default GLListPanelEnhanced;
